// Reads the PDF417 barcode off a license photo entirely client-side, using the browser's
// native BarcodeDetector where available. Ported from the compliance portal's onboarding
// wizard (tryBarcode). Free and immediate when it works; the caller falls back to the
// server-side vision extract when it does not.

// readPdf417(file) -> Promise<string|null>. Never throws: unsupported browsers, a bad image,
// or no barcode found all resolve to null so the caller can move straight to the next fallback.
export async function readPdf417(file) {
  try {
    if (!("BarcodeDetector" in window)) return null;
    try {
      const supported = await window.BarcodeDetector.getSupportedFormats();
      if (supported && supported.length && !supported.includes("pdf417")) return null;
    } catch (e) {
      // getSupportedFormats itself is optional in some implementations; try detecting anyway.
    }

    let bitmap;
    try {
      bitmap = await createImageBitmap(file);
    } catch (e) {
      return null;
    }

    const detector = new window.BarcodeDetector({ formats: ["pdf417"] });
    const candidates = [];
    const maxDim = Math.max(bitmap.width, bitmap.height);
    for (const target of [1600, 2400]) {
      if (maxDim > target) {
        const scale = target / maxDim;
        const w = Math.round(bitmap.width * scale);
        const h = Math.round(bitmap.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
        candidates.push(canvas);
      }
    }
    candidates.push(bitmap);

    for (let pass = 0; pass < 2; pass++) {
      for (const img of candidates) {
        try {
          const codes = await detector.detect(img);
          if (codes && codes.length) {
            for (const code of codes) {
              if (code.rawValue) return code.rawValue;
            }
          }
        } catch (e) {
          // this candidate did not decode, try the next one
        }
      }
    }
    return null;
  } catch (e) {
    return null;
  }
}
